import api from './api';

/**
 * User Actions Service
 * Handles save-to-contact, mark-as-purchased, share, and wishlist functionality
 */

// ==================== WISHLIST FUNCTIONALITY ====================

export const saveToWishlist = async (productId, wishlistId = null, notes = '') => {
    try {
        const payload = { product: productId };
        if (wishlistId) payload.wishlist_id = wishlistId;
        if (notes) payload.notes = notes;

        const response = await api.post('/wishlists/items/', payload);
        return response.data;
    } catch (error) {
        console.error('Error saving to wishlist:', error);
        throw error;
    }
};

export const removeFromWishlist = async (productId, wishlistId = null) => {
    try {
        const params = new URLSearchParams({ product: productId });
        if (wishlistId) params.append('wishlist_id', wishlistId);

        const response = await api.delete(`/wishlists/items/?${params}`);
        return response.data;
    } catch (error) {
        console.error('Error removing from wishlist:', error);
        throw error;
    }
};

export const getUserWishlists = async () => {
    try {
        const response = await api.get('/wishlists/');
        return response.data;
    } catch (error) {
        console.error('Error fetching user wishlists:', error);
        throw error;
    }
};

export const createWishlistWithProduct = async (wishlistName, productId) => {
    try {
        const response = await api.post('/wishlists/', {
            name: wishlistName,
            initial_product: productId
        });
        return response.data;
    } catch (error) {
        console.error('Error creating wishlist with product:', error);
        throw error;
    }
};

// ==================== SAVE TO CONTACT FUNCTIONALITY ====================

export const saveToContact = async (productId, contactId, occasion = null, notes = '') => {
    try {
        const payload = {
            product: productId,
            notes: notes
        };
        if (occasion) payload.occasion = occasion;

        const response = await api.post(`/contacts/${contactId}/gift-ideas/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving to contact:', error);
        throw error;
    }
};

export const removeFromContact = async (contactId, giftIdeaId) => {
    try {
        const response = await api.delete(`/contacts/${contactId}/gift-ideas/${giftIdeaId}/`);
        return response.data;
    } catch (error) {
        console.error('Error removing from contact:', error);
        throw error;
    }
};

export const getContactGiftIdeas = async (contactId) => {
    try {
        const response = await api.get(`/contacts/${contactId}/gift-ideas/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching contact gift ideas:', error);
        throw error;
    }
};

export const getUserContacts = async () => {
    try {
        const response = await api.get('/contacts/details/');
        return response.data;
    } catch (error) {
        console.error('Error fetching user contacts:', error);
        throw error;
    }
};

// ==================== MARK AS PURCHASED FUNCTIONALITY ====================

export const markAsPurchased = async (productId, contactId = null, occasionId = null, purchaseDetails = {}) => {
    try {
        const payload = {
            product: productId,
            gift_name: purchaseDetails.giftName || '',
            description: purchaseDetails.description || '',
            price: purchaseDetails.price || 0,
            purchase_date: purchaseDetails.purchaseDate || new Date().toISOString().split('T')[0],
            gift_date: purchaseDetails.giftDate || null,
            notes: purchaseDetails.notes || ''
        };

        if (contactId) payload.contact = contactId;
        if (occasionId) payload.occasion = occasionId;

        const response = await api.post('/contacts/gift-history/', payload);
        return response.data;
    } catch (error) {
        console.error('Error marking as purchased:', error);
        throw error;
    }
};

export const updatePurchaseRecord = async (recordId, updates) => {
    try {
        const response = await api.patch(`/contacts/gift-history/${recordId}/`, updates);
        return response.data;
    } catch (error) {
        console.error('Error updating purchase record:', error);
        throw error;
    }
};

export const getPurchaseHistory = async () => {
    try {
        const response = await api.get('/contacts/gift-history/');
        return response.data;
    } catch (error) {
        console.error('Error fetching purchase history:', error);
        throw error;
    }
};

export const getProductPurchaseStatus = async (productId) => {
    try {
        const response = await api.get(`/contacts/gift-history/?product=${productId}`);
        return response.data.length > 0;
    } catch (error) {
        console.error('Error checking purchase status:', error);
        return false;
    }
};

// ==================== SHARE FUNCTIONALITY ====================

export const shareProduct = async (productId, platform = 'link', customMessage = '') => {
    try {
        const payload = {
            product: productId,
            platform: platform,
            message: customMessage
        };

        const response = await api.post('/shares/', payload);
        return response.data;
    } catch (error) {
        console.error('Error sharing product:', error);
        throw error;
    }
};

export const createShareLink = async (productId, expirationDays = 30) => {
    try {
        const payload = {
            product: productId,
            expires_in_days: expirationDays
        };

        const response = await api.post('/shares/create-link/', payload);
        return response.data;
    } catch (error) {
        console.error('Error creating share link:', error);
        throw error;
    }
};

export const getShareAnalytics = async (shareId) => {
    try {
        const response = await api.get(`/shares/${shareId}/analytics/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching share analytics:', error);
        throw error;
    }
};

// ==================== COMBINED USER STATE FUNCTIONALITY ====================

export const getUserProductState = async (productId) => {
    try {
        const [wishlistStatus, purchaseStatus, contactSaves] = await Promise.allSettled([
            checkWishlistStatus(productId),
            getProductPurchaseStatus(productId),
            getProductContactSaves(productId)
        ]);

        return {
            isInWishlist: wishlistStatus.status === 'fulfilled' ? wishlistStatus.value : false,
            isPurchased: purchaseStatus.status === 'fulfilled' ? purchaseStatus.value : false,
            savedContacts: contactSaves.status === 'fulfilled' ? contactSaves.value : [],
            error: {
                wishlist: wishlistStatus.status === 'rejected' ? wishlistStatus.reason : null,
                purchase: purchaseStatus.status === 'rejected' ? purchaseStatus.reason : null,
                contacts: contactSaves.status === 'rejected' ? contactSaves.reason : null
            }
        };
    } catch (error) {
        console.error('Error fetching user product state:', error);
        return {
            isInWishlist: false,
            isPurchased: false,
            savedContacts: [],
            error: error
        };
    }
};

const checkWishlistStatus = async (productId) => {
    try {
        const response = await api.get(`/wishlists/items/?product=${productId}`);
        return response.data.length > 0;
    } catch (error) {
        return false;
    }
};

const getProductContactSaves = async (productId) => {
    try {
        const response = await api.get(`/contacts/gift-ideas/?product=${productId}`);
        return response.data.map(save => ({
            contactId: save.contact.id,
            contactName: save.contact.name,
            savedDate: save.created_at,
            occasion: save.occasion
        }));
    } catch (error) {
        return [];
    }
};

// ==================== BATCH OPERATIONS ====================

export const batchSaveToWishlist = async (productIds, wishlistId) => {
    try {
        const promises = productIds.map(productId =>
            saveToWishlist(productId, wishlistId)
        );
        const results = await Promise.allSettled(promises);

        return {
            successful: results.filter(r => r.status === 'fulfilled').map(r => r.value),
            failed: results.filter(r => r.status === 'rejected').map(r => r.reason)
        };
    } catch (error) {
        console.error('Error in batch save to wishlist:', error);
        throw error;
    }
};

export const batchShareProducts = async (productIds, platform = 'link') => {
    try {
        const promises = productIds.map(productId =>
            shareProduct(productId, platform)
        );
        const results = await Promise.allSettled(promises);

        return {
            successful: results.filter(r => r.status === 'fulfilled').map(r => r.value),
            failed: results.filter(r => r.status === 'rejected').map(r => r.reason)
        };
    } catch (error) {
        console.error('Error in batch share products:', error);
        throw error;
    }
};

// ==================== UTILITY FUNCTIONS ====================

export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
    }
};

export const showSuccessToast = (message) => {
    // TODO: Integrate with actual toast notification system
    console.log('Success:', message);
};

export const showErrorToast = (message) => {
    // TODO: Integrate with actual toast notification system
    console.error('Error:', message);
};

export default {
    // Wishlist
    saveToWishlist,
    removeFromWishlist,
    getUserWishlists,
    createWishlistWithProduct,

    // Contact gift ideas
    saveToContact,
    removeFromContact,
    getContactGiftIdeas,
    getUserContacts,

    // Purchase tracking
    markAsPurchased,
    updatePurchaseRecord,
    getPurchaseHistory,
    getProductPurchaseStatus,

    // Sharing
    shareProduct,
    createShareLink,
    getShareAnalytics,

    // Combined state
    getUserProductState,

    // Batch operations
    batchSaveToWishlist,
    batchShareProducts,

    // Utilities
    copyToClipboard,
    showSuccessToast,
    showErrorToast
}; 